import { useEffect, useRef, useState } from 'react';
import { Spinner } from './Spinner';

export interface NewQuoteFormValues {
  phone: string;
  first_name: string;
  last_name?: string;
  amount: number;
  service_label: string;
  vehicle?: string;
  send_recap: boolean;
}

interface NewQuoteModalProps {
  open: boolean;
  busy?: boolean;
  /** Optional service chip presets (same as LogQuoteModal). */
  servicePresets?: string[];
  onCancel: () => void;
  onConfirm: (values: NewQuoteFormValues) => void;
}

/** Loose 10+ digit US phone validator — final E.164 normalization happens
 *  on the Worker side. Just gates the submit button against obvious junk. */
function isPlausiblePhone(raw: string): boolean {
  const digits = raw.replace(/\D/g, '');
  return digits.length >= 10 && digits.length <= 15;
}

/** Format a phone string as "(XXX) XXX-XXXX" while preserving partial input. */
function formatPhoneInput(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 11);
  // Strip leading "1" for US 11-digit format so display stays clean.
  const usable = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (usable.length === 0) return '';
  if (usable.length <= 3) return `(${usable}`;
  if (usable.length <= 6) return `(${usable.slice(0, 3)}) ${usable.slice(3)}`;
  return `(${usable.slice(0, 3)}) ${usable.slice(3, 6)}-${usable.slice(6, 10)}`;
}

/**
 * Cold owner-handled call quote entry. Used when the customer doesn't
 * exist in the PWA Conversations list yet — the owner picks up a call
 * himself (telco dipped through, personal cell, walk-in), takes the
 * quote, and wants it logged + the customer enrolled in the recap+drip
 * automation.
 *
 * Form: Phone (required, formatted), First name (required), Last name
 * (optional), Amount, Service (with chip presets), Vehicle (optional),
 * "Also send recap text + start drip" checkbox (default ON).
 *
 * Worker handles find-or-create on the contact by phone, so the same
 * customer calling twice from the same number lands on the same GHL
 * contact record — no duplicates.
 */
export function NewQuoteModal({
  open,
  busy = false,
  servicePresets,
  onCancel,
  onConfirm,
}: NewQuoteModalProps) {
  const [phone, setPhone] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [amount, setAmount] = useState('');
  const [serviceLabel, setServiceLabel] = useState('');
  const [vehicle, setVehicle] = useState('');
  // 2026-06-01 — default OFF (was ON). Same rationale as LogQuoteModal —
  // owner explicitly opts in per-quote when they want the recap + drip.
  const [sendRecap, setSendRecap] = useState(false);
  const [touched, setTouched] = useState(false);

  const phoneInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open) {
      setPhone('');
      setFirstName('');
      setLastName('');
      setAmount('');
      setServiceLabel('');
      setVehicle('');
      setSendRecap(false);
      setTouched(false);
      window.setTimeout(() => phoneInputRef.current?.focus(), 40);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  const phoneValid = isPlausiblePhone(phone);
  const firstNameValid = firstName.trim().length > 0;
  const parsedAmount = Number.parseFloat(amount.replace(/[^0-9.]/g, ''));
  const amountValid = Number.isFinite(parsedAmount) && parsedAmount > 0;
  const serviceValid = serviceLabel.trim().length > 0;
  const canSubmit =
    phoneValid && firstNameValid && amountValid && serviceValid && !busy;

  const handleSubmit = () => {
    setTouched(true);
    if (!canSubmit) return;
    // Hand the raw phone to the Worker — it normalizes to E.164. Just
    // strip non-digits so we don't ship the "(203) 555-0100" formatting.
    const phoneDigits = phone.replace(/\D/g, '');
    onConfirm({
      phone: phoneDigits,
      first_name: firstName.trim(),
      last_name: lastName.trim() ? lastName.trim() : undefined,
      amount: parsedAmount,
      service_label: serviceLabel.trim(),
      vehicle: vehicle.trim() ? vehicle.trim() : undefined,
      send_recap: sendRecap,
    });
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="new-quote-dialog-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/40 px-4 backdrop-blur-sm"
      onClick={() => {
        if (!busy) onCancel();
      }}
    >
      <div
        className="max-h-[90vh] w-full max-w-sm overflow-y-auto rounded-ss-lg bg-white p-5 shadow-ss-md dark:bg-[#1E293B]"
        onClick={(e) => e.stopPropagation()}
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        <h2
          id="new-quote-dialog-title"
          className="text-base font-bold text-ink-900 dark:text-white"
        >
          New quote
        </h2>
        <p className="mt-1 text-xs leading-relaxed text-ink-600 dark:text-[#94A3B8]">
          For customers you quoted off-Shelby and don't see in your conversations
          yet. We'll create the contact and optionally send the recap + drip.
        </p>

        {/* Phone */}
        <label className="mt-4 block">
          <span className="text-xs font-semibold text-ink-700 dark:text-[#CBD5E1]">
            Phone
          </span>
          <input
            ref={phoneInputRef}
            type="tel"
            inputMode="tel"
            autoComplete="off"
            value={phone}
            placeholder="(203) 555-0100"
            onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
            className="mt-1 w-full rounded-ss-sm border border-ink-200 bg-white px-3 py-2 text-sm text-ink-900 placeholder:text-ink-400 focus:border-ss-blue focus:outline-none focus:ring-2 focus:ring-ss-blue/20 dark:border-[#334155] dark:bg-[#0F172A] dark:text-white"
          />
          {touched && !phoneValid ? (
            <span className="mt-1 block text-xs text-status-attention">
              Enter a valid phone number (10+ digits).
            </span>
          ) : null}
        </label>

        {/* Name (first required, last optional, side-by-side) */}
        <div className="mt-3 grid grid-cols-2 gap-2">
          <label className="block">
            <span className="text-xs font-semibold text-ink-700 dark:text-[#CBD5E1]">
              First name
            </span>
            <input
              type="text"
              autoComplete="off"
              value={firstName}
              placeholder="Lori"
              onChange={(e) => setFirstName(e.target.value)}
              className="mt-1 w-full rounded-ss-sm border border-ink-200 bg-white px-3 py-2 text-sm text-ink-900 placeholder:text-ink-400 focus:border-ss-blue focus:outline-none focus:ring-2 focus:ring-ss-blue/20 dark:border-[#334155] dark:bg-[#0F172A] dark:text-white"
            />
            {touched && !firstNameValid ? (
              <span className="mt-1 block text-xs text-status-attention">
                Required.
              </span>
            ) : null}
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-ink-700 dark:text-[#CBD5E1]">
              Last name <span className="font-normal text-ink-400">(opt)</span>
            </span>
            <input
              type="text"
              autoComplete="off"
              value={lastName}
              placeholder=""
              onChange={(e) => setLastName(e.target.value)}
              className="mt-1 w-full rounded-ss-sm border border-ink-200 bg-white px-3 py-2 text-sm text-ink-900 placeholder:text-ink-400 focus:border-ss-blue focus:outline-none focus:ring-2 focus:ring-ss-blue/20 dark:border-[#334155] dark:bg-[#0F172A] dark:text-white"
            />
          </label>
        </div>

        {/* Amount */}
        <label className="mt-3 block">
          <span className="text-xs font-semibold text-ink-700 dark:text-[#CBD5E1]">
            Quoted amount
          </span>
          <div className="relative mt-1">
            <span
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-ink-500 dark:text-[#94A3B8]"
            >
              $
            </span>
            <input
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={amount}
              placeholder="399.99"
              onChange={(e) => setAmount(e.target.value)}
              className="w-full rounded-ss-sm border border-ink-200 bg-white py-2 pl-7 pr-3 text-sm text-ink-900 placeholder:text-ink-400 focus:border-ss-blue focus:outline-none focus:ring-2 focus:ring-ss-blue/20 dark:border-[#334155] dark:bg-[#0F172A] dark:text-white"
            />
          </div>
          {touched && !amountValid ? (
            <span className="mt-1 block text-xs text-status-attention">
              Enter an amount greater than 0.
            </span>
          ) : null}
        </label>

        {/* Service */}
        <label className="mt-3 block">
          <span className="text-xs font-semibold text-ink-700 dark:text-[#CBD5E1]">
            Service
          </span>
          <input
            type="text"
            autoComplete="off"
            value={serviceLabel}
            placeholder="Premium Detail"
            onChange={(e) => setServiceLabel(e.target.value)}
            className="mt-1 w-full rounded-ss-sm border border-ink-200 bg-white px-3 py-2 text-sm text-ink-900 placeholder:text-ink-400 focus:border-ss-blue focus:outline-none focus:ring-2 focus:ring-ss-blue/20 dark:border-[#334155] dark:bg-[#0F172A] dark:text-white"
          />
          {servicePresets && servicePresets.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {servicePresets.map((preset) => {
                const active = serviceLabel === preset;
                return (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => setServiceLabel(preset)}
                    className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                      active
                        ? 'border-ss-blue bg-ss-blue text-white'
                        : 'border-ink-200 bg-ink-50 text-ink-700 hover:bg-ink-100 dark:border-[#334155] dark:bg-[#1E293B] dark:text-[#CBD5E1] dark:hover:bg-[#334155]'
                    }`}
                  >
                    {preset}
                  </button>
                );
              })}
            </div>
          ) : null}
          {touched && !serviceValid ? (
            <span className="mt-1 block text-xs text-status-attention">
              Pick or type the service you quoted.
            </span>
          ) : null}
        </label>

        {/* Vehicle */}
        <label className="mt-3 block">
          <span className="text-xs font-semibold text-ink-700 dark:text-[#CBD5E1]">
            Vehicle <span className="font-normal text-ink-400">(optional)</span>
          </span>
          <input
            type="text"
            autoComplete="off"
            value={vehicle}
            placeholder="2018 Jeep Wrangler"
            onChange={(e) => setVehicle(e.target.value)}
            className="mt-1 w-full rounded-ss-sm border border-ink-200 bg-white px-3 py-2 text-sm text-ink-900 placeholder:text-ink-400 focus:border-ss-blue focus:outline-none focus:ring-2 focus:ring-ss-blue/20 dark:border-[#334155] dark:bg-[#0F172A] dark:text-white"
          />
        </label>

        {/* Send recap */}
        <label className="mt-4 flex items-start gap-2.5 rounded-ss-sm border border-ink-200 bg-ink-50 px-3 py-2.5 dark:border-[#334155] dark:bg-[#0F172A]">
          <input
            type="checkbox"
            checked={sendRecap}
            onChange={(e) => setSendRecap(e.target.checked)}
            className="mt-0.5 h-4 w-4 accent-ss-blue"
          />
          <span className="text-xs leading-relaxed text-ink-700 dark:text-[#CBD5E1]">
            <span className="font-semibold">Also send recap text + start drip</span>
            <br />
            <span className="text-ink-500 dark:text-[#94A3B8]">
              Uncheck if they asked not to be texted.
            </span>
          </span>
        </label>

        {/* Actions */}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={onCancel}
            className="rounded-ss-sm border border-ink-200 bg-white px-4 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-50 disabled:opacity-50 dark:border-[#334155] dark:bg-[#1E293B] dark:text-[#CBD5E1] dark:hover:bg-[#334155]"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={handleSubmit}
            className="inline-flex items-center justify-center gap-2 rounded-ss-sm bg-ss-blue px-4 py-2 text-sm font-semibold text-white hover:bg-ss-blue-dark disabled:opacity-70"
          >
            {busy ? (
              <>
                <Spinner />
                <span>Saving…</span>
              </>
            ) : (
              'Save quote'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
